import { faTimes } from "@fortawesome/pro-light-svg-icons";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { NoStyleButton } from "@aviary/components/Button";
import { Typography } from "@aviary/components/Typography";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { l } from "@aviary/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome";

import { AttachmentFileIcon } from "./AttachmentFileIcon";
import { DeprecatedAttachment } from "./DeprecatedAttachment";

import * as styles from "./Attachment.styles";

const fileTypeColors = ["png", "pdf", "jpeg", "jpg"];

const iconColorMatch = (fileType: string) => {
  return fileTypeColors.includes(fileType) ? fileType : "default";
};

const getFileType = (fileName: string) => {
  const splitResult = (fileName ?? "").split(".");

  return splitResult.length > 1 ? splitResult.pop() : "";
};

interface Props {
  /**
   * Name of the file
   */
  fileName: string;
  /**
   * Callback for the conditional remove button is clicked
   */
  handleRemove?: () => void;
  /**
   * URL which enables viewing image in a new tab
   */
  thumbnailUrl?: string;
  /**
   * Show the full label without a limit
   *
   * @default false
   */
  showFullLabel?: boolean;
  /**
   * @deprecated
   * DS3-only: Render a child element on the right side
   */
  rightAction?: ReactNode;
  /**
   * Icon or image to show instead of folder one
   */
  customIcon?: ReactNode;
  /**
   * Optional callback to fire on item click
   */
  onItemClick?: () => void;
  /**
   * Boolean to hide the file type subtitle
   */
  hideFileType?: boolean;
  /**
   * Optional display property to force the width to be 12.5rem
   */
  isWide?: boolean;
  /**
   * Optional display property to force the width to be full width
   */
  isFullWidth?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/inputs/Attachment
 */
const Attachment = (props: Props) => {
  const { t } = useTranslation();
  const isDS4Enabled = useIsDS4Enabled();

  if (!isDS4Enabled) {
    return <DeprecatedAttachment {...props} />;
  }

  const {
    fileName,
    handleRemove,
    thumbnailUrl,
    showFullLabel,
    customIcon,
    onItemClick,
    hideFileType,
    isWide,
    isFullWidth,
    ...rest
  } = props;

  const fileType = getFileType(fileName);

  const renderFilename = () => {
    const splitResult = fileName.split(".");
    let justName = fileName;
    if (splitResult.length > 1) {
      // contains file extension
      const regexFileExtension = /\.[0-9a-z]+$/i;
      justName = fileName.replace(regexFileExtension, "");
    }

    return justName;
  };

  const renderFileType = () => (fileType && !hideFileType ? `.${fileType}` : "");

  const renderIcon = () => {
    if (customIcon) {
      return customIcon;
    }

    return <AttachmentFileIcon />;
  };

  const renderFileInformation = () => (
    <>
      <div css={styles.iconWrapper}>{renderIcon()}</div>
      <div css={styles.documentInfo}>
        <Typography
          isSecondaryWeight
          type="footnote"
          isMarginless
          data-testid="filename"
          title={fileName}
          css={[
            !isFullWidth && styles.truncate,
            isFullWidth && styles.truncateFullWidth,
            showFullLabel && styles.showLabel,
          ]}
        >
          {renderFilename()}
          {renderFileType()}
        </Typography>
      </div>
    </>
  );

  const renderFileArea = () => {
    if (thumbnailUrl) {
      return (
        <a
          css={[
            styles.attachmentArea,
            isWide && styles.buttonIsWide,
            isFullWidth && styles.buttonIsFullWidth,
          ]}
          href={thumbnailUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="document-link"
        >
          {renderFileInformation()}
        </a>
      );
    }
    if (onItemClick) {
      return (
        <NoStyleButton
          css={[
            styles.attachmentArea,
            isWide && styles.buttonIsWide,
            isFullWidth && styles.buttonIsFullWidth,
          ]}
          onClick={onItemClick}
          data-testid="document-button"
        >
          {renderFileInformation()}
        </NoStyleButton>
      );
    }

    return (
      <div
        css={[
          styles.attachmentArea,
          isWide && styles.buttonIsWide,
          isFullWidth && styles.buttonIsFullWidth,
        ]}
      >
        {renderFileInformation()}
      </div>
    );
  };

  return (
    <div
      css={[
        styles.attachment,
        isWide && styles.attachmentIsWide,
        isFullWidth && styles.attachmentIsFullWidth,
      ]}
      aria-label={t(l.aviary.attachment.attachmentName, { name: fileName })}
      {...rest}
    >
      {renderFileArea()}
      {handleRemove && (
        <NoStyleButton
          css={styles.removableArea}
          onClick={handleRemove}
          aria-label={t(l.aviary.attachment.removeAttachment, { name: fileName })}
          data-testid="close-button"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </NoStyleButton>
      )}
    </div>
  );
};

export type { Props };
export { Attachment, getFileType, iconColorMatch };
